"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
// jobs.json hardcodes WordPress image URLs on the raw IP host, which is not in
// next.config.js `remotePatterns` — passing them straight to next/image threw
// "Invalid src prop" and 500'd the whole page. Every other WP-image consumer
// routes through this helper (→ /api/wp-media). Do not drop it.
import { sanitizeImageUrl } from "@utils/imageUrl";
import Link from "next/link";
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Briefcase,
} from "lucide-react";
import jobData from "@data/pages/jobs.json";

// Dynamic imports for better performance
import dynamic from "next/dynamic";
const HeaderPublic = dynamic(() => import("@components/layout/HeaderPublic"), {
  ssr: true,
});
const FooterPublic = dynamic(() => import("@components/layout/FooterPublic"), {
  ssr: true,
});
const Modal = dynamic(() => import("@components/modals/Modal"), {
  ssr: false,
});

import { useModal } from "@hooks/useModal";
import {
  JobApplicationButton,
  ExternalLinkButton,
  CalendarButton,
} from "@components/ui/IconButtonVariants";

// ---------------------
// TypeScript Interfaces
// ---------------------

interface FeaturedImage {
  src: string;
  alt: string;
}

interface JobDetails {
  location: string;
  employmentType: string;
  description: string;
  payment: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
  applyLink: string;
}

interface JobListing {
  id: number;
  slug: string;
  title: string;
  category: string;
  tags: string[];
  excerpt: string;
  featuredImage: FeaturedImage;
  date: string;
  publisher: string;
  details: JobDetails;
}

interface HeroSection {
  title: string;
  accentText: string;
  subtitle: string;
  primaryButton: {
    text: string;
    action: string;
    href: string;
  };
  secondaryButton: {
    text: string;
    action: string;
    href: string;
  };
}

interface CTASection {
  seoChip: string;
  title: string;
  description: string;
  benefits: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  primaryButton: {
    text: string;
    action: string;
    href: string;
  };
  secondaryButton: {
    text: string;
    action: string;
    href: string;
  };
}

interface JobListingsSection {
  title: string;
  subtitle: string;
  emptyState: {
    title: string;
    subtitle: string;
  };
}

interface JobData {
  heroSection: HeroSection;
  ctaSection: CTASection;
  jobListingsSection: JobListingsSection;
  jobListings: JobListing[];
}

// ---------------------
// JobOverview Component
// ---------------------

export default function JobOverview() {
  const router = useRouter();
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  const jobModal = useModal();

  // Type Assertion for jobData
  const data: JobData = jobData as JobData;
  const jobListings: JobListing[] = data.jobListings;

  // Sort jobListings by id in ascending order
  const sortedJobListings = useMemo(() => {
    return [...jobListings].sort((a, b) => a.id - b.id);
  }, [jobListings]);

  // URL-based modal handling - optimized like Homepage
  useEffect(() => {
    if (typeof window === "undefined") return;

    const urlParams = new URLSearchParams(window.location.search);
    const jobSlug = urlParams.get("job");

    if (jobSlug) {
      const job = sortedJobListings.find((j) => j.slug === jobSlug);
      if (job) {
        setSelectedJob(job);
        jobModal.open();
      } else {
        console.warn(`Job with slug "${jobSlug}" not found.`);
        router.replace("/jobs");
      }
    }
  }, [sortedJobListings, jobModal, router]);

  // Browser History Support
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const jobSlug = urlParams.get("job");

      if (jobSlug) {
        const job = sortedJobListings.find((j) => j.slug === jobSlug);
        if (job) {
          setSelectedJob(job);
          jobModal.open();
        }
      } else {
        jobModal.close();
        setSelectedJob(null);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [sortedJobListings, jobModal]);

  // Modal handlers - Best Practices wie Homepage/Landingpage
  const handleCardClick = (job: JobListing) => {
    setSelectedJob(job);
    jobModal.open();
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", `/jobs?job=${job.slug}`);
    }
  };

  const handleJobClick = (job: JobListing) => {
    handleCardClick(job);
  };

  const closeDialog = () => {
    jobModal.close();
    setSelectedJob(null);
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/jobs");
    }
  };

  // LinkedIn Jobs URL from the search results
  return (
    <>
      <HeaderPublic />

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section
          className="relative bg-gradient-to-br from-abis-primary to-abis-primary/90 py-20 md:py-44 overflow-hidden"
          role="banner"
          aria-labelledby="hero-title"
        >
          <div
            className="absolute inset-0 bg-gradient-to-r from-abis-accent/10 via-transparent to-abis-accent/10"
            aria-hidden="true"
          ></div>
          <div className="container mx-auto pt-28 pb-10 px-4 max-w-6xl relative z-10">
            <div className="text-center">
              <h1
                id="hero-title"
                className="text-[2rem] md:text-[3rem] lg:text-[3rem] font-normal text-white mb-6"
                style={{
                  fontFamily: "var(--font-kanit, Kanit, sans-serif)",
                }}
                itemProp="headline"
              >
                {data.heroSection.title}
                <span className="text-abis-accent">{data.heroSection.accentText}</span>
              </h1>
              <p
                className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8"
                itemProp="description"
              >
                {data.heroSection.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <JobApplicationButton
                  href={data.heroSection.primaryButton.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel={data.heroSection.primaryButton.text}
                >
                  {data.heroSection.primaryButton.text}
                </JobApplicationButton>
                <Link
                  href={data.heroSection.secondaryButton.href}
                  className="btn btn-hero bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold py-4 px-8 rounded-xl text-lg border border-white/20 hover:border-white/40 whitespace-nowrap"
                >
                  {data.heroSection.secondaryButton.text}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Job Listings Section */}
        <section
          id="job-listings"
          className="py-20 bg-gray-50"
          aria-labelledby="job-listings-title"
        >
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-16">
              <h2
                id="job-listings-title"
                className="section-header"
                itemProp="name"
              >
                <span className="text-abis-primary">
                  {data.jobListingsSection.title}
                </span>
              </h2>
              <p
                className="text-lg text-gray-600 max-w-2xl mx-auto"
                itemProp="description"
              >
                {sortedJobListings.length} {data.jobListingsSection.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedJobListings && sortedJobListings.length > 0 ? (
                sortedJobListings.map((job: JobListing) => (
                  <div
                    key={job.id}
                    className="bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group border border-gray-100"
                    onClick={() => handleJobClick(job)}
                  >
                    {/* Job Image */}
                    <div
                      className="relative h-48 overflow-hidden cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJobClick(job);
                      }}
                    >
                      <Image
                        src={sanitizeImageUrl(job.featuredImage.src)}
                        alt={job.featuredImage.alt}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-abis-accent text-primary px-3 py-1 rounded-full text-sm font-semibold">
                          {job.category}
                        </span>
                      </div>
                    </div>

                    {/* Job Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {job.title}
                      </h3>

                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {job.excerpt}
                      </p>

                      {/* Job Meta */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="w-4 h-4 mr-2" />
                          {job.details.location}
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="w-4 h-4 mr-2" />
                          {job.details.employmentType}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {job.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                        {job.tags.length > 3 && (
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs">
                            +{job.tags.length - 3} mehr
                          </span>
                        )}
                      </div>

                      {/* Crawlable role details: the modal is client-only, so
                          this collapsed block is what gets the ~200 words per
                          role into the server HTML for search engines. */}
                      <details
                        className="mb-4 text-sm text-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <summary className="cursor-pointer font-semibold text-gray-700">
                          Aufgaben, Anforderungen & Benefits
                        </summary>
                        <div className="mt-3 space-y-3">
                          <p>{job.details.description}</p>
                          <p className="font-semibold text-gray-700 mb-1">Aufgaben</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {job.details.responsibilities.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                          <p className="font-semibold text-gray-700 mb-1">Anforderungen</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {job.details.requirements.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                          <p className="font-semibold text-gray-700 mb-1">Wir bieten</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {job.details.benefits.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </details>

                      {/* Apply Button */}
                      <JobApplicationButton
                        className="w-full"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleJobClick(job);
                        }}
                        ariaLabel="Details ansehen"
                      >
                        Details ansehen
                      </JobApplicationButton>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg text-gray-600">
                    {data.jobListingsSection.emptyState.title}
                  </p>
                  <p className="text-gray-500 mt-2">
                    {data.jobListingsSection.emptyState.subtitle}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section - Recruiting */}
        <section className="py-20 bg-gradient-to-br from-abis-primary to-abis-primary/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-abis-accent/10 via-transparent to-abis-accent/10"></div>
          <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <div className="text-center">
              {/* SEO Chip */}
              <div className="inline-flex items-center px-4 py-2 bg-abis-accent/20 backdrop-blur-sm rounded-full mb-6">
                <Users className="w-5 h-5 text-abis-accent mr-2" />
                <span className="text-abis-accent font-semibold text-xs uppercase tracking-wide">
                  {data.ctaSection.seoChip}
                </span>
              </div>

              {/* Title */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                {data.ctaSection.title}
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-12">
                {data.ctaSection.description}
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {data.ctaSection.benefits.map((benefit, index) => {
                  const IconComponent =
                    benefit.icon === "Briefcase"
                      ? Briefcase
                      : benefit.icon === "Users"
                      ? Users
                      : benefit.icon === "Calendar"
                      ? Calendar
                      : Briefcase;
                  return (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-abis-primary/30 via-abis-primary/45 to-abis-primary/35 rounded-2xl p-6 border border-primary shadow-lg"
                    >
                      <IconComponent className="w-8 h-8 text-abis-accent mx-auto mb-4" />
                      <h3 className="text-white font-semibold mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {benefit.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <ExternalLinkButton
                  href={data.ctaSection.primaryButton.href}
                  variant="primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel={data.ctaSection.primaryButton.text}
                >
                  {data.ctaSection.primaryButton.text}
                </ExternalLinkButton>
                <CalendarButton
                  href={data.ctaSection.secondaryButton.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel={data.ctaSection.secondaryButton.text}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/20 hover:border-white/40"
                >
                  {data.ctaSection.secondaryButton.text}
                </CalendarButton>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Job Details Modal - Außerhalb des Grid-Containers für korrekte Zentrierung */}
      <Modal
        isOpen={jobModal.isOpen}
        onClose={closeDialog}
        title={selectedJob?.title || "Job Details"}
        variant="form"
        size="lg"
        className="max-w-4xl"
        fullscreenOnMobile={true}
        showCloseButton={true}
        closeOnOverlayClick={true}
        closeOnEscape={true}
      >
        {selectedJob ? (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Job Info */}
              <div className="space-y-6">
                {/* Job Image */}
                <div className="relative h-64 rounded-xl overflow-hidden">
                  <Image
                    src={sanitizeImageUrl(selectedJob.featuredImage.src)}
                    alt={selectedJob.featuredImage.alt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>

                {/* Job Meta */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Job-Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">
                        {selectedJob.details.location}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">
                        {selectedJob.details.employmentType}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Briefcase className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">
                        {selectedJob.category}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Apply Button */}
                <JobApplicationButton
                  href={selectedJob.details.applyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                  ariaLabel="Jetzt bewerben"
                >
                  Jetzt bewerben
                </JobApplicationButton>
              </div>

              {/* Right Column - Job Description */}
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Beschreibung
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedJob.details.description}
                  </p>
                </div>

                {/* Responsibilities */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Verantwortlichkeiten
                  </h3>
                  <ul className="space-y-2">
                    {selectedJob.details.responsibilities.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-2 h-2 bg-abis-accent rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Requirements */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Qualifikationen
                  </h3>
                  <ul className="space-y-2">
                    {selectedJob.details.requirements.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-2 h-2 bg-abis-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Benefits
                  </h3>
                  <ul className="space-y-2">
                    {selectedJob.details.benefits.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            <p className="text-gray-600">Job-Details werden geladen...</p>
          </div>
        )}
      </Modal>

      <FooterPublic />
    </>
  );
}
